// 他参加者の選択バッジの行を、選択枠（ペンストロークは選択輪郭）の上側の輪郭へ密着させる位置を求める純粋関数群。
//
// バッジの行は「先頭バッジの下面中央」を輪郭上の 1 点（接点）へ合わせて置く。接点の決め方は次のとおり。
// - 枠（角丸の矩形。回転を含む）: 上側の輪郭のうち、最上点から 0.5 画面 px 以内の部分を候補にし、
//   「外接矩形の左端 + 先頭バッジの幅 / 2」に x が最も近い点を接点にする。回転していない十分な幅の枠では
//   候補の中にこの x があるため、行の左端が枠の左端にそろい、行の下端が枠の上辺に接する。
//   全角度で同じ決め方を使い、0° だけを別の方式にしない（わずかな回転で行が半バッジ分跳ぶのを防ぐ）。
// - ペンストローク: 選択リングの外縁の最上点（回転後のアウトラインの頂点のうち最も上の点から、
//   リングの膨張量だけ上）。同じ高さの頂点が複数あるときは左の点を採る。
//
// 座標は、特に断りが無い限りステージ座標（px）で扱う。画面 px で決まっている量（許容幅・比較の幅・
// 先頭バッジの幅・リングの膨張量）は、倍率で割ってステージ座標へそろえてから使う。
// DOM とストアに依存しないため、境界条件を単体テストで検証できる。

/** ステージ座標の点。 */
export interface StagePoint {
  readonly x: number;
  readonly y: number;
}

/** ステージ座標の矩形（回転前の外接矩形。回転は中心まわり）。 */
export interface StageBox {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/** 他参加者の選択枠の角丸の半径（ステージ px）。枠の borderRadius と一致させる。 */
export const REMOTE_SELECTION_FRAME_RADIUS_PX = 8;

/** 接点の候補にする、輪郭の最上点からの許容幅（画面 px）。 */
export const BADGE_CONTACT_TOLERANCE_SCREEN_PX = 0.5;

/** 同じ値とみなして比べる幅（画面 px）。 */
export const BADGE_CONTACT_EPSILON_SCREEN_PX = 0.01;

/** 二分探索の反復回数。区間の幅を 2^-60 倍まで縮めれば、ステージ座標の範囲では誤差が無視できる。 */
const BISECTION_STEPS = 60;

/** 浮動小数の誤差で輪郭の端を取りこぼさないための、判定のゆとり（ステージ px）。 */
const GEOMETRY_SLACK = 1e-9;

/** 表示位置の丸めの単位。最大倍率（16 倍）でも画面上 0.02 px 未満の誤差に収まる。 */
const POSITION_PRECISION = 1000;

/**
 * 倍率の逆数を返す。正の有限値でなければ 1（`--canvas-inv-zoom` の既定値と同じ）とする。
 */
export function safeInverseZoom(zoom: number): number {
  return Number.isFinite(zoom) && zoom > 0 ? 1 / zoom : 1;
}

function finiteOr(value: number | undefined, fallback: number): number {
  return value !== undefined && Number.isFinite(value) ? value : fallback;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * CSS の border-radius と同じく、辺の長さの半分を超えないよう縮めた角丸の半径（ステージ px）。
 * 角丸は画面固定ではなくステージ px の値で、倍率に合わせて画面上の大きさも変わる。
 */
export function frameCornerRadius(box: StageBox): number {
  const w = Math.max(0, finiteOr(box.w, 0));
  const h = Math.max(0, finiteOr(box.h, 0));
  return Math.min(REMOTE_SELECTION_FRAME_RADIUS_PX, w / 2, h / 2);
}

interface RotatedRoundedRect {
  /** 角丸の中心を結ぶ内側の矩形の 4 隅（回転後）。左上・右上・右下・左下の順。 */
  readonly corners: readonly StagePoint[];
  /** 内側の矩形の各辺（上・右・下・左）の外向き単位法線（回転後）。 */
  readonly normals: readonly StagePoint[];
  readonly radius: number;
}

/** 中心まわりに回転した角丸の矩形を、内側の矩形と半径で表す（角丸の矩形 = 内側の矩形 ⊕ 半径の円）。 */
function rotatedRoundedRect(box: StageBox, rotationDeg: number): RotatedRoundedRect {
  const w = Math.max(0, finiteOr(box.w, 0));
  const h = Math.max(0, finiteOr(box.h, 0));
  const radius = frameCornerRadius(box);
  const halfW = w / 2 - radius;
  const halfH = h / 2 - radius;
  const centerX = finiteOr(box.x, 0) + w / 2;
  const centerY = finiteOr(box.y, 0) + h / 2;
  const theta = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  // CSS の rotate(θ) と同じ向き（画面座標で時計回り）に回す。
  const rotate = (x: number, y: number): StagePoint => ({ x: x * cos - y * sin, y: x * sin + y * cos });
  const corners = [
    [-halfW, -halfH],
    [halfW, -halfH],
    [halfW, halfH],
    [-halfW, halfH],
  ].map(([x = 0, y = 0]) => {
    const rotated = rotate(x, y);
    return { x: centerX + rotated.x, y: centerY + rotated.y };
  });
  const normals = [
    [0, -1],
    [1, 0],
    [0, 1],
    [-1, 0],
  ].map(([x = 0, y = 0]) => rotate(x, y));
  return { corners, normals, radius };
}

/**
 * 角丸の矩形の上側の輪郭（その x での最小の y）。輪郭の外側の x では Infinity。
 * 角丸の矩形の輪郭は「各隅の円の上半分」と「各辺を外向きに半径だけずらした線分」から成るため、
 * その x で両者がとる y の最小値が上側の輪郭になる（どちらも図形に含まれる点なので、最小値は輪郭上の点）。
 */
function upperContourY(shape: RotatedRoundedRect, x: number): number {
  const { corners, normals, radius } = shape;
  let best = Number.POSITIVE_INFINITY;
  for (const corner of corners) {
    const dx = x - corner.x;
    if (Math.abs(dx) <= radius + GEOMETRY_SLACK) {
      best = Math.min(best, corner.y - Math.sqrt(Math.max(0, radius * radius - dx * dx)));
    }
  }
  for (let index = 0; index < corners.length; index += 1) {
    const from = corners[index];
    const to = corners[(index + 1) % corners.length];
    const normal = normals[index];
    if (!from || !to || !normal) continue;
    const ax = from.x + normal.x * radius;
    const ay = from.y + normal.y * radius;
    const bx = to.x + normal.x * radius;
    const by = to.y + normal.y * radius;
    // 縦の辺（と長さ 0 の辺）は、両端の隅の円が同じ x の範囲を受け持つ。
    if (Math.abs(bx - ax) <= GEOMETRY_SLACK) continue;
    if (x < Math.min(ax, bx) - GEOMETRY_SLACK || x > Math.max(ax, bx) + GEOMETRY_SLACK) continue;
    const t = clamp((x - ax) / (bx - ax), 0, 1);
    best = Math.min(best, ay + (by - ay) * t);
  }
  return best;
}

/**
 * 区間 [from, to] で「上側の輪郭が threshold 以下」になる境目を二分探索する。
 * 上側の輪郭は凸関数で、from 側では threshold を超え、to 側（最上点）では threshold 以下であること。
 */
function bisectContourBoundary(
  shape: RotatedRoundedRect,
  threshold: number,
  from: number,
  to: number
): number {
  let outside = from;
  let inside = to;
  for (let step = 0; step < BISECTION_STEPS; step += 1) {
    const middle = (outside + inside) / 2;
    if (upperContourY(shape, middle) <= threshold) inside = middle;
    else outside = middle;
  }
  return inside;
}

/**
 * 枠（角丸の矩形）に対する接点を求める。
 *
 * @param box 枠の外接矩形（回転前、ステージ座標）。テキスト図形の高さは実測した表示高さを渡す。
 * @param rotationDeg 枠の回転角（度、中心まわり）。未指定・非有限は 0。
 * @param firstBadgeWidth 先頭バッジの幅（ステージ px）。
 * @param zoom 現在の倍率（画面 px とステージ px の換算に使う）。
 */
export function computeFrameBadgeContact(params: {
  readonly box: StageBox;
  readonly rotationDeg: number | undefined;
  readonly firstBadgeWidth: number;
  readonly zoom: number;
}): StagePoint {
  const inverseZoom = safeInverseZoom(params.zoom);
  const shape = rotatedRoundedRect(params.box, finiteOr(params.rotationDeg, 0));
  const { corners, radius } = shape;
  const xs = corners.map((corner) => corner.x);
  const ys = corners.map((corner) => corner.y);
  const left = Math.min(...xs) - radius;
  const right = Math.max(...xs) + radius;
  const top = Math.min(...ys) - radius;
  const halfBadge = Math.max(0, finiteOr(params.firstBadgeWidth, 0)) / 2;
  const desiredX = left + halfBadge;

  // 最上点の x。上辺が水平な場合は上辺の上のどの点でもよい（候補の区間の内側にあればよい）。
  let topCorner = corners[0] ?? { x: left, y: top };
  for (const corner of corners) {
    if (corner.y < topCorner.y) topCorner = corner;
  }
  const threshold =
    top + (BADGE_CONTACT_TOLERANCE_SCREEN_PX + BADGE_CONTACT_EPSILON_SCREEN_PX) * inverseZoom;
  // 候補（上側の輪郭のうち threshold 以下の部分）の x の区間。上側の輪郭は凸なので区間は 1 つ。
  const candidateLeft =
    upperContourY(shape, left) <= threshold
      ? left
      : bisectContourBoundary(shape, threshold, left, topCorner.x);
  const candidateRight =
    upperContourY(shape, right) <= threshold
      ? right
      : bisectContourBoundary(shape, threshold, right, topCorner.x);
  // 候補の中で x が最も近い点は、希望の x を区間へ収めた点として 1 つに決まる。
  const x = clamp(desiredX, candidateLeft, candidateRight);
  const y = upperContourY(shape, x);
  return { x, y: Number.isFinite(y) ? y : top };
}

/**
 * ペンストロークに対する接点を求める。頂点が無い（空のアウトライン）ときは null を返し、
 * 呼び出し側は枠と同じ求め方（外接矩形の上辺）へ戻す。
 *
 * @param vertices 選択輪郭と同じアウトラインの閉多角形の頂点（回転前、ステージ座標）。
 * @param box ストロークの外接矩形（回転の中心を決める）。
 * @param rotationDeg 回転角（度、中心まわり）。
 * @param ringOuterScreenPx アウトラインから選択リングの外縁までの距離（画面 px）。
 * @param zoom 現在の倍率。
 */
export function computePenBadgeContact(params: {
  readonly vertices: readonly StagePoint[];
  readonly box: StageBox;
  readonly rotationDeg: number | undefined;
  readonly ringOuterScreenPx: number;
  readonly zoom: number;
}): StagePoint | null {
  const inverseZoom = safeInverseZoom(params.zoom);
  const epsilon = BADGE_CONTACT_EPSILON_SCREEN_PX * inverseZoom;
  const w = Math.max(0, finiteOr(params.box.w, 0));
  const h = Math.max(0, finiteOr(params.box.h, 0));
  const centerX = finiteOr(params.box.x, 0) + w / 2;
  const centerY = finiteOr(params.box.y, 0) + h / 2;
  const theta = (finiteOr(params.rotationDeg, 0) * Math.PI) / 180;
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  const rotated: StagePoint[] = [];
  for (const vertex of params.vertices) {
    if (!Number.isFinite(vertex.x) || !Number.isFinite(vertex.y)) continue;
    const dx = vertex.x - centerX;
    const dy = vertex.y - centerY;
    rotated.push({ x: centerX + dx * cos - dy * sin, y: centerY + dx * sin + dy * cos });
  }
  if (rotated.length === 0) return null;
  // 頂点は数百〜千程度あり得るため、可変長引数へ展開せずに走査で最小値を求める。
  let minY = Number.POSITIVE_INFINITY;
  for (const vertex of rotated) minY = Math.min(minY, vertex.y);
  // 最も上の頂点。同じ高さ（比較の幅の内側）の頂点が複数あるときは左の点を採る。
  let chosen: StagePoint | null = null;
  for (const vertex of rotated) {
    if (vertex.y > minY + epsilon) continue;
    if (chosen === null || vertex.x < chosen.x) chosen = vertex;
  }
  if (chosen === null) return null;
  // リングは頂点を中心にした円（丸い角）で膨らむため、外縁の最上点は最上の頂点のちょうど真上にある。
  // アウトライン自体が筆圧と太さを含むため、太さは足さない。
  const ringOuter = Math.max(0, finiteOr(params.ringOuterScreenPx, 0)) * inverseZoom;
  return { x: chosen.x, y: chosen.y - ringOuter };
}

/**
 * 接点から、バッジの行の位置（枠の外接矩形の左上を原点とするステージ座標）を求める。
 * left は行の左端、top は行の下端の y（行の下端を接点の高さへ置く）。
 */
export function badgeRowOffset(
  contact: StagePoint,
  box: StageBox,
  firstBadgeWidth: number
): { readonly left: number; readonly top: number } {
  const halfBadge = Math.max(0, finiteOr(firstBadgeWidth, 0)) / 2;
  return {
    left: roundPosition(contact.x - finiteOr(box.x, 0) - halfBadge),
    top: roundPosition(contact.y - finiteOr(box.y, 0)),
  };
}

/** 表示位置を丸める（浮動小数の誤差で 0 が -0 や 1e-15 になり、style が毎回変わるのを防ぐ）。 */
function roundPosition(value: number): number {
  if (!Number.isFinite(value)) return 0;
  const rounded = Math.round(value * POSITION_PRECISION) / POSITION_PRECISION;
  return rounded === 0 ? 0 : rounded;
}
